/**
 * Pure derivations over the server's redacted read model.
 *
 * Everything here takes the `SessionState` the server sent and answers a
 * question a screen has. Nothing here decides what is visible — that decision
 * was made on the server and arrived already applied, with hidden rows carrying
 * no `value` key at all. These functions read that verdict; they never
 * reconstruct it.
 *
 * The one derivation that deliberately stops short is `everyoneIn`. A screen may
 * know that every present participant has committed and still not be revealed,
 * because the reveal is a server fact and this device may be holding an
 * unsynced write. That gap has a name and a sentence on screen rather than a
 * client-side shortcut.
 */

import {
  aspectProfile,
  disagreement,
  distribution,
  overallValue,
  topicValue,
} from "../../../shared/aggregate.ts";
import type {
  AspectSlice,
  Distribution,
  OverallValue,
  RatingCell,
  TopicContribution,
  TopicValue,
} from "../../../shared/aggregate.ts";
import type { RadarAxis, RadarSeries } from "../../components/Radar.tsx";
import type {
  AspectKind,
  CaptureMode,
  SessionParticipant,
  Stop,
} from "../../../shared/types.ts";
import type {
  AspectView,
  HeadlineView,
  OverallView,
  RatingView,
  SessionState,
  SubQuestionView,
} from "../../../server/sessions.ts";

export type { AspectView, HeadlineView, OverallView, RatingView, SessionState, SubQuestionView };

/**
 * The 1–10 "desire to play again" ladder. The one mandatory number.
 *
 * Ten values, ten different sentences. This used to carry seven: 4 and 5 both
 * said "If asked", 6 and 7 both "Happy to", 8 and 9 both "Would suggest it".
 * That was an oversight and not a decision — it arrived whole in the first pass
 * at these screens, with no note beside it, and nothing in `docs/PLAN.md`
 * asks for it. Decision #8 there names "would suggest it / would play if asked
 * / would veto" as *anchors* for the ends and the middle of the scale, which is
 * an instruction to make the numbers mean something, not to spend one phrase on
 * two of them.
 *
 * It cost more than tidiness. The rating control used to publish the stop label
 * as the slider's `aria-valuetext`, so a rater using a screen reader heard "If
 * asked" twice in a row and had no way to tell 4 from 5 — three of the ten rungs
 * were unreachable by ear, on the one rating in this app that nobody may skip.
 * The written chips under the track had the same problem in the same place.
 *
 * The control announces the rating itself now ("3.5 out of 5 stars"), so the
 * duplication no longer reaches a rater mid-gesture. These labels did not stop
 * mattering, though: they are what the results screens say a night *meant*, and
 * two rungs sharing a phrase still collapse two findings into one sentence.
 *
 * The new rungs are placed so that no stored number moves far. Every label that
 * used to cover a pair now sits on one of the two values it already covered —
 * "If asked" 4-5 → 5, "Happy to" 6-7 → 7, "Would suggest it" 8-9 → 8 — so a 7
 * from last month still reads as "Happy to" and nothing already in the database
 * is re-described by more than one step.
 *
 * Register is the group's, not the product's: these are things somebody actually
 * says when the box is going back on the shelf, which is the whole point of a
 * behavioural anchor over a number. Sentence case, no full stops, and short
 * enough to survive a tenth of a phone's width.
 */
export const HEADLINE_STOPS: Stop[] = [
  { value: 1, label: "Would veto" },
  { value: 2, label: "Not again" },
  { value: 3, label: "Only if pushed" },
  { value: 4, label: "If nothing else" },
  { value: 5, label: "If asked" },
  { value: 6, label: "Wouldn't mind" },
  { value: 7, label: "Happy to" },
  { value: 8, label: "Would suggest it" },
  { value: 9, label: "Would push for it" },
  { value: 10, label: "Tonight, again" },
];

/**
 * The vocabulary for the night's overall — how good the evening was, which is a
 * different question from whether anybody wants a rematch.
 *
 * It needs its own ladder rather than borrowing the headline's, because the
 * headline's rungs are all about *next time*: "Would veto" against a night that
 * happened is not a judgement anybody makes. A group can have a fine evening at a
 * game they will never set up again, and the two figures sitting side by side on
 * the results screen are only worth having if they can disagree.
 *
 * Same register and same ten values, so a figure that is a mean of six topics
 * still lands on a rung somebody would say out loud.
 */
export const OVERALL_STOPS: Stop[] = [
  { value: 1, label: "A washout" },
  { value: 2, label: "Rough night" },
  { value: 3, label: "Mostly flat" },
  { value: 4, label: "Passable" },
  { value: 5, label: "Decent enough" },
  { value: 6, label: "A good night" },
  { value: 7, label: "Really good" },
  { value: 8, label: "One of the good ones" },
  { value: 9, label: "A great night" },
  { value: 10, label: "One to remember" },
];

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

/**
 * The aspect a step's note belongs to, and how to name it on screen.
 *
 * Set on exactly one step per aspect — the last one in its block — and null on
 * every other. That is what makes the note *per category* rather than per
 * question: an aspect and its follow-ups are one subject, and being asked "say
 * it in words" once per screen invites four fragments of the same thought
 * instead of one sentence about the thing.
 *
 * Where a block is a lone aspect — which is every block in the seeded taxonomy,
 * since nothing seeds sub-questions — this lands on the aspect's own step, the
 * words go to `aspect_rating.note`, and nothing about the write changes.
 *
 * A block with follow-ups ends somewhere other than where that row lives, and
 * the write is **not** redirected: the note goes to whatever row the last step
 * of the block owns, which for a follow-up is `subquestion_rating.note`. One
 * note per category on screen, stored under the last question of the category.
 * Redirecting it would mean writing the aspect row from a step that may not
 * read it — on a shared device the stars on that row are hidden, so the write
 * would have to merge a value it is not allowed to show — and that is a piece
 * of machinery worth building against a real taxonomy rather than against an
 * imagined one. Nothing seeds sub-questions today, so nothing reaches it yet;
 * settle it with the category restructure that will.
 */
export interface NoteBlock {
  aspectTitle: string;
}

/** A step that carries an answer of its own, which is every step but the headline. */
type RatedStep = Extract<Step, { kind: "aspect" | "subquestion" }>;

export type Step =
  | { kind: "aspect"; key: string; title: string; view: AspectView; noteBlock?: NoteBlock | null }
  | {
      kind: "subquestion";
      key: string;
      title: string;
      /** The aspect this hangs off, for the line that gives it context. */
      parentTitle: string;
      /**
       * Optional rather than required because a step is also a shape other
       * screens hand-build to render one row — `Results` does — and a note is a
       * question only the rating loop asks. Absent reads the same as null: no
       * note here. `buildSteps` sets it on every step it makes, either way.
       */
      noteBlock?: NoteBlock | null;
      /**
       * Inherited from the parent aspect, because a sub-question has no kind of
       * its own. It matters: decision #1 says a descriptive axis is never
       * averaged, and a follow-up under a descriptive aspect is describing the
       * same thing more finely. Defaulting to evaluative would quietly put a
       * mean under it.
       */
      parentKind: AspectKind;
      view: SubQuestionView;
    }
  | { kind: "headline"; key: "headline"; title: string; view: HeadlineView };

/**
 * Every follow-up under the topic it hangs off, each block in the order asked.
 *
 * Pulled out of `buildSteps` because three other things now need the same
 * grouping — the derived topic value, the screen that offers to go into detail,
 * and the count in "worked out from 3 of 6 answers" — and a second grouping built
 * elsewhere would be a second answer to "which questions belong to Pacing".
 */
export function followUpsByAspect(state: SessionState): Map<string, SubQuestionView[]> {
  const followUps = new Map<string, SubQuestionView[]>();
  for (const view of state.subquestions ?? []) {
    const parent = view.subquestion.session_aspect_id;
    const list = followUps.get(parent);
    if (list) list.push(view);
    else followUps.set(parent, [view]);
  }
  for (const list of followUps.values()) {
    list.sort((a, b) => a.subquestion.sort_snapshot - b.subquestion.sort_snapshot);
  }
  return followUps;
}

/**
 * The rating loop, category-major.
 *
 * Aspects first, in their snapshot order, each one immediately followed by its
 * own sub-questions — a follow-up only means anything next to the thing it
 * follows, and asking "how did the tension actually play out" three screens
 * after Tension makes the group answer it cold.
 *
 * The headline is last because it is the summary judgement and the group has
 * just spent several screens arguing about the parts — and it is its own step,
 * never derived from anything above it.
 *
 * ## `detailed` is what keeps a six-topic night from being forty-three screens
 *
 * The shipped base set is six topics with six follow-ups each. Walking all of
 * them at every seat is forty-two questions per person before the headline, and
 * a rating ritual that long is one nobody finishes — which costs more than the
 * detail was worth, because a night abandoned on step nine has nothing on it at
 * all. So the topics are the spine and the follow-ups are a branch: pass the set
 * of topic ids whose follow-ups this rater has opened, and only those blocks
 * expand. A topic left closed is answered on its own step, in one gesture.
 *
 * Omitting the argument includes everything, which is what every reader outside
 * the rating loop wants — `subQuestionSlices` builds the results screen out of
 * this and has to see the questions whether or not this device opened them.
 */
export function buildSteps(state: SessionState, detailed?: ReadonlySet<string>): Step[] {
  const followUps = followUpsByAspect(state);

  const steps: Step[] = [];
  for (const view of state.aspects) {
    const block: RatedStep[] = [
      {
        kind: "aspect",
        key: view.aspect.id,
        title: view.aspect.name_snapshot,
        view,
        noteBlock: null,
      },
    ];
    const opened = detailed === undefined || detailed.has(view.aspect.id);
    for (const sub of opened ? (followUps.get(view.aspect.id) ?? []) : []) {
      block.push({
        kind: "subquestion",
        key: sub.subquestion.id,
        title: sub.subquestion.name_snapshot,
        parentTitle: view.aspect.name_snapshot,
        parentKind: view.aspect.kind_snapshot,
        view: sub,
        noteBlock: null,
      });
    }
    // The note goes on the way out of the block, not on the way in: the sentence
    // somebody has about Interaction is the one they have after the follow-ups
    // have made them think about it, and asking on the first screen gets the
    // first half of it. `block` is never empty — the aspect step is always in it
    // — and holds no headline: that one is appended below, outside any block, and
    // `RatedStep` is what keeps the compiler from having to be told so here.
    block[block.length - 1]!.noteBlock = { aspectTitle: view.aspect.name_snapshot };
    steps.push(...block);
  }
  steps.push({
    kind: "headline",
    key: "headline",
    title: "Desire to play again",
    view: state.headline,
  });
  return steps;
}

/**
 * How far the step somebody is standing on moves when topics open beneath them.
 *
 * A step is a position in a list, so inserting a topic's six follow-ups *above*
 * where you stand silently hands you a different question. That is not a
 * hypothetical: the loop re-opens a topic whose follow-ups this seat already
 * answered, which fires the moment a seat is claimed, and "I'm olli — my turn"
 * on step 2 of 7 landed on step 2 of 13 — the first follow-up under the previous
 * topic — where the next rating typed in overwrote an answer to a question
 * nobody had opened.
 *
 * Only blocks starting strictly before `index` count. A topic opening at or
 * after it puts its questions ahead of the rater, which is where they belong,
 * and a topic opening exactly where they stand puts them after that step too.
 *
 * Pure and exported so the arithmetic has a test: the bug it fixes is a
 * navigation jump no assertion in this suite could have seen.
 */
export function stepShiftOnOpen(
  steps: readonly Step[],
  index: number,
  opening: readonly string[],
  followUps: ReadonlyMap<string, readonly SubQuestionView[]>,
): number {
  let shift = 0;
  for (const aspectId of opening) {
    const at = steps.findIndex((s) => s.kind === "aspect" && s.key === aspectId);
    if (at >= 0 && at < index) shift += followUps.get(aspectId)?.length ?? 0;
  }
  return shift;
}

export function stopsOf(step: Step): Stop[] {
  if (step.kind === "headline") return HEADLINE_STOPS;
  return step.kind === "subquestion"
    ? step.view.subquestion.stops_snapshot
    : step.view.aspect.stops_snapshot;
}

/** Only an aspect carries a polarity. A sub-question's ladder runs one way. */
export function isBipolar(step: Step): boolean {
  return step.kind === "aspect" && step.view.aspect.polarity_snapshot === "BIPOLAR";
}

/**
 * N/A is offered on everything except the headline — the one thing nobody skips.
 *
 * Sub-questions are as skippable as their aspects, and for the same reason: the
 * reveal gate counts committed rows, so "doesn't apply" has to be writable or a
 * follow-up nobody can answer keeps the whole table blind.
 */
export function allowsNa(step: Step): boolean {
  return step.kind !== "headline";
}

// ---------------------------------------------------------------------------
// Progress and the reveal
// ---------------------------------------------------------------------------

export interface Progress {
  /** Straight from the server. Never inferred. */
  revealed: boolean;
  committed: Set<string>;
  waiting: SessionParticipant[];
  /**
   * Everyone this device knows about has committed. NOT the same as revealed:
   * a shared device holding unsynced writes lands here and waits.
   */
  everyoneIn: boolean;
}

export function progressOf(
  ratings: readonly RatingView[],
  revealed: boolean,
  participants: readonly SessionParticipant[],
): Progress {
  const committed = new Set(ratings.map((r) => r.participant_id));
  const waiting = participants.filter((p) => !committed.has(p.id));
  return {
    revealed,
    committed,
    waiting,
    everyoneIn: participants.length > 0 && waiting.length === 0,
  };
}

/**
 * A topic's follow-ups count towards it, and leaving them out deadlocks the
 * night.
 *
 * A rater who goes into detail writes six `subquestion_rating` rows and no
 * `aspect_rating` row at all, so a roster built from `step.view.ratings` alone
 * has them still waiting on a topic they just answered six times. On separate
 * phones that is a wrong line; on one phone going round the table it is a stall
 * with no way out — `nextRater` reads the same `waiting` list, so "Done — pass
 * it on" hands the phone straight back to the person who is already finished,
 * for as long as anybody keeps tapping. That is what it did, and nothing on
 * screen said why.
 *
 * The server settles a seat on the same union (`settledOnTopicCount` in
 * `sessions.ts`), which is the point: the two must not disagree about who is
 * holding the table up, or the reveal lands while the roster still says WAITING.
 * `progressOf` takes the set, so a seat that did both counts once.
 *
 * Required rather than defaulted: every caller has the grouping to hand, and a
 * forgotten argument would restore the stall silently.
 */
export function progressOfStep(
  step: Step,
  participants: readonly SessionParticipant[],
  followUps: readonly SubQuestionView[],
): Progress {
  const ratings = [...step.view.ratings, ...followUps.flatMap((sub) => sub.ratings)];
  return progressOf(ratings, step.view.revealed, participants);
}

/**
 * Who the table is waiting on, phrased for the device it is on.
 *
 * Same rule, two presentations: on a shared device the answer is an instruction
 * to a person in the room, on separate phones it is a status about someone
 * else's screen.
 */
export function waitingLabel(
  mode: CaptureMode,
  waiting: readonly SessionParticipant[],
): string | null {
  if (waiting.length === 0) return null;
  const first = waiting[0]!;
  if (mode === "SHARED_DEVICE") {
    return waiting.length === 1
      ? `Pass the phone to ${first.name_snapshot}`
      : `Pass the phone to ${first.name_snapshot} — then ${waiting.length - 1} more`;
  }
  if (waiting.length === 1) return `Waiting on ${first.name_snapshot}`;
  if (waiting.length === 2) return `Waiting on ${first.name_snapshot} and ${waiting[1]!.name_snapshot}`;
  return `Waiting on ${first.name_snapshot} and ${waiting.length - 1} others`;
}

/**
 * The next person who has not answered, for the shared-device hand-off.
 *
 * `followUps` comes before `after` deliberately: it is the argument that decides
 * whether the answer is right, and `after` only decides where the ring starts.
 * Both are positional, so the one that can be forgotten is the one that must not
 * be optional.
 */
export function nextRater(
  step: Step,
  participants: readonly SessionParticipant[],
  followUps: readonly SubQuestionView[],
  after?: string | null,
): SessionParticipant | null {
  const { waiting } = progressOfStep(step, participants, followUps);
  if (waiting.length === 0) return null;
  if (!after) return waiting[0]!;
  const order = participants.map((p) => p.id);
  const from = order.indexOf(after);
  for (let i = 1; i <= order.length; i++) {
    const id = order[(from + i) % order.length]!;
    const found = waiting.find((p) => p.id === id);
    if (found) return found;
  }
  return waiting[0]!;
}

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

export interface Cell {
  participant: SessionParticipant;
  /** False while this is somebody else's blind entry. */
  visible: boolean;
  committed: boolean;
  value: number | null;
  na: boolean;
  note: string | null;
}

/**
 * One row per participant, in seat order, whether or not they have answered.
 *
 * A redacted row has no `value` key at all, so `visible` is read rather than
 * guessed at from a null — "hidden" and "rated n/a" are different states and
 * the screen shows different things for them.
 */
export function cellsOf(step: Step, participants: readonly SessionParticipant[]): Cell[] {
  const byId = new Map(step.view.ratings.map((r) => [r.participant_id, r] as const));
  return participants.map((participant) => {
    const row = byId.get(participant.id);
    if (!row) {
      return {
        participant,
        visible: false,
        committed: false,
        value: null,
        na: false,
        note: null,
      };
    }
    return {
      participant,
      visible: row.visible,
      committed: true,
      value: row.visible ? (row.value ?? null) : null,
      na: row.visible ? row.na === 1 : false,
      note: row.visible ? (row.note ?? null) : null,
    };
  });
}

/**
 * The same rows for a topic, with a derived value standing in where the rater
 * gave one through the follow-ups instead of on the topic itself.
 *
 * Only after the reveal, and the gate is not tidiness. Before it, the one row
 * this device may read is the actor's own — the server marks it visible so the
 * control can show it back — and a derived value built out of *that* row put on
 * the shared strip would print the answer of whoever just handed the phone over.
 * A blind step therefore keeps exactly the cells it has always had.
 */
export function topicCellsOf(
  view: AspectView,
  followUps: readonly SubQuestionView[],
  participants: readonly SessionParticipant[],
): Cell[] {
  const step: Step = {
    kind: "aspect",
    key: view.aspect.id,
    title: view.aspect.name_snapshot,
    view,
  };
  const base = cellsOf(step, participants);
  if (!view.revealed) return base;
  return base.map((cell) => {
    const derived = topicValueOf(view, followUps, cell.participant.id);
    if (derived.source === "set") return cell;
    if (derived.value === null && !derived.na) return cell;
    return { ...cell, visible: true, committed: true, value: derived.value, na: derived.na };
  });
}

export function ownCell(step: Step, participantId: string | null): Cell | null {
  if (!participantId) return null;
  const row = step.view.ratings.find((r) => r.participant_id === participantId);
  if (!row) return null;
  return {
    participant: {
      id: participantId,
      name_snapshot: "",
      colour_snapshot: "",
    } as SessionParticipant,
    visible: row.visible,
    committed: true,
    value: row.value ?? null,
    na: row.na === 1,
    note: row.note ?? null,
  };
}

/**
 * Has this seat answered this step — the same union the reveal gate uses.
 *
 * A topic answered only through its follow-ups has no row of its own, so
 * `ownCell` alone says "not answered" about a seat that filled in all six
 * questions under it. The table's reveal already knows better
 * (`progressOfStep`, `settledOnTopicCount` on the server), and a third rule
 * here would land a rater on a topic the night considers finished, next to a
 * worked-out number that is already theirs.
 *
 * Takes the whole grouping rather than one step's list: every caller is asking
 * about a run of steps, and a per-step lookup at the call site is where the
 * `.kind === "aspect"` check gets forgotten.
 */
export function answeredStep(
  step: Step,
  participantId: string | null,
  followUps: ReadonlyMap<string, readonly SubQuestionView[]>,
): boolean {
  if (!participantId) return false;
  if (ownCell(step, participantId)) return true;
  if (step.kind !== "aspect") return false;
  const subs = followUps.get(step.key) ?? [];
  return subs.some((sub) => sub.ratings.some((r) => r.participant_id === participantId));
}

// ---------------------------------------------------------------------------
// Aggregates for the results screen
// ---------------------------------------------------------------------------

/**
 * Visible rating cells for one aspect.
 *
 * Only visible rows go in, so an unrevealed aspect yields at most the actor's
 * own row — the server marks that one visible. That single row is not a group
 * distribution, so callers must gate aggregation on `revealed` rather than on
 * `n`; charting it would be one opinion wearing the table's clothes.
 */
export function ratingCells(view: AspectView | SubQuestionView | HeadlineView): RatingCell[] {
  const cells: RatingCell[] = [];
  for (const r of view.ratings) {
    if (!r.visible) continue;
    cells.push({ value: r.value ?? null, na: (r.na ?? 0) as 0 | 1 });
  }
  return cells;
}

/**
 * The rail's data from the roster's, so the two cannot disagree.
 *
 * The rating screen draws a distribution beside the list of seats, and both have
 * to come out of one array — build the rail from the rows and the roster from
 * the cells and the mean marker lands where no dot is. So this takes the cells
 * the roster is already rendering rather than re-reading the view.
 *
 * The rule it applies is the one `topicCells` applies a level up: silence is not
 * a cell, an n/a is. A seat with nothing readable contributes nothing, because
 * `aspectProfile` counts a null as a skip that has already been through the
 * gate — which is a different claim from "we cannot see this yet".
 */
export function visibleRatingCells(cells: readonly Cell[]): RatingCell[] {
  const out: RatingCell[] = [];
  for (const cell of cells) {
    if (!cell.visible) continue;
    if (cell.value === null && !cell.na) continue;
    out.push({ value: cell.value, na: cell.na ? 1 : 0 });
  }
  return out;
}

/**
 * One person's cell on one question, or null where they have nothing readable.
 *
 * `visible` is the whole gate. A redacted row arrives with no `value` and no
 * `na` key at all, so reading it would not merely show a hidden rating — it
 * would silently count as a null, which is a different answer from "hidden".
 */
function cellFor(
  view: AspectView | SubQuestionView,
  participantId: string,
): RatingCell | null {
  const row = view.ratings.find((r) => r.participant_id === participantId);
  if (!row || !row.visible) return null;
  return { value: row.value ?? null, na: (row.na ?? 0) as 0 | 1 };
}

/**
 * What one seat's number for one topic is tonight, and where it came from.
 *
 * The reveal is deliberately *not* consulted here. This is the function the
 * rating screen asks about the person holding the phone, mid-night, before
 * anything has revealed — their own rows are the ones the server marks visible,
 * so the redaction has already done the gating and asking twice would blank the
 * one answer this screen exists to show back. Every caller that draws somebody
 * else's number — the radar, the results distribution — gates on
 * `view.revealed` itself, exactly as it did before there was anything derived.
 */
export function topicValueOf(
  view: AspectView,
  followUps: readonly SubQuestionView[],
  participantId: string,
): TopicValue {
  const own = cellFor(view, participantId);
  const cells: RatingCell[] = [];
  for (const sub of followUps) {
    const cell = cellFor(sub, participantId);
    if (cell) cells.push(cell);
  }
  return topicValue(view.aspect.kind_snapshot, own, cells);
}

/**
 * The night's overall for one seat: their override, or the mean of their topics.
 *
 * The reveal is not consulted, for the same reason `topicValueOf` does not
 * consult it — the rows this reads are already redacted, and a seat's own rows
 * are the ones the server marks visible. Gating a second time here would blank
 * the figure on the screen that exists to show it back.
 *
 * The redaction is also why this cannot be reconstructed elsewhere from the
 * override table alone: most seats never write a row, so an empty `state.overall`
 * means "derived", never "not answered". `overallValue` is the one place the two
 * halves are combined; this only feeds it.
 */
export function overallValueOf(
  state: SessionState,
  participantId: string,
  followUps: ReadonlyMap<string, readonly SubQuestionView[]>,
): OverallValue {
  const row = state.overall.ratings.find((r) => r.participant_id === participantId);
  const override = row && row.visible && typeof row.value === "number" ? row.value : null;
  const topics: TopicContribution[] = state.aspects.map((view) => ({
    kind: view.aspect.kind_snapshot,
    value: topicValueOf(view, followUps.get(view.aspect.id) ?? [], participantId),
  }));
  return overallValue(override, topics);
}

/**
 * One roster row per seat for the night's overall, in seat order.
 *
 * The one derivation, feeding both halves of the results block: the rail's dots
 * and the roster's lines are `aspectProfile(...)` and `cells` over this same
 * array. A second function working the distribution out from the state directly
 * is how a block ends up saying "n = 5" over a roster naming three.
 *
 *
 * Not `cellsOf`: there is no step here and most seats have no row at all, so a
 * reader of `state.overall.ratings` alone would report the whole table as
 * unanswered on a night where everybody rated everything. Every figure on this
 * screen has to come from the same place the rail's dots do, or the block
 * disagrees with itself.
 *
 * `committed` tracks the derived value rather than a stored row for the same
 * reason. A seat with nothing to say gets a `null` and reads as no answer, which
 * is what it is — silence, not a zero.
 *
 * `followUps` is optional only so a test or a one-off caller need not build it.
 * Every screen already has one memoised, and rebuilding it here walks every
 * follow-up on the night a second time for no answer that differs.
 */
export function overallCells(
  state: SessionState,
  followUpsIn?: Map<string, SubQuestionView[]>,
): Cell[] {
  const followUps = followUpsIn ?? followUpsByAspect(state);
  return state.participants.map((participant) => {
    const derived = overallValueOf(state, participant.id, followUps);
    return {
      participant,
      visible: derived.value !== null,
      committed: derived.value !== null,
      value: derived.value,
      na: false,
      note: null,
    };
  });
}

/**
 * The topic's distribution across the table, built from derived values.
 *
 * A seat with nothing to say contributes no cell rather than a null one: an
 * absent answer is silence, and `aspectProfile` counts a null as a skip that has
 * already been through the gate. Same rule `ratingCells` has always had, applied
 * one level up.
 */
export function topicCells(
  view: AspectView,
  followUps: readonly SubQuestionView[],
  participants: readonly SessionParticipant[],
): RatingCell[] {
  const cells: RatingCell[] = [];
  for (const participant of participants) {
    const derived = topicValueOf(view, followUps, participant.id);
    if (derived.value === null && !derived.na) continue;
    cells.push({ value: derived.value, na: derived.na ? 1 : 0 });
  }
  return cells;
}

/**
 * Every topic as the results screen reads it.
 *
 * The ratings are the derived values now, not the raw `aspect_rating` rows: a
 * rater who went into detail has no row on the topic at all, and reading the
 * table straight would have shown their topic as unanswered while their six
 * follow-ups sat right underneath it saying otherwise.
 */
export function aspectSlices(state: SessionState): AspectSlice[] {
  const followUps = followUpsByAspect(state);
  return state.aspects.map((view) => ({
    id: view.aspect.id,
    name: view.aspect.name_snapshot,
    kind: view.aspect.kind_snapshot,
    stops: view.aspect.stops_snapshot,
    ratings: topicCells(view, followUps.get(view.aspect.id) ?? [], state.participants),
  }));
}

/**
 * The same slices for the follow-ups, in the order the group was asked them.
 *
 * Kept apart from `aspectSlices` rather than merged into it: ranking a
 * sub-question against the aspects would float a follow-up above the thing it
 * follows, and "Turn pressure" on its own, three sections from Tension, is a
 * finding without its subject. The results screen lists them under their parent
 * instead.
 */
export function subQuestionSlices(state: SessionState): (AspectSlice & { parent: string })[] {
  return buildSteps(state).flatMap((step) =>
    step.kind === "subquestion"
      ? [
          {
            id: step.view.subquestion.id,
            name: step.title,
            parent: step.parentTitle,
            kind: step.parentKind,
            stops: step.view.subquestion.stops_snapshot,
            ratings: ratingCells(step.view),
          },
        ]
      : [],
  );
}

/**
 * The ink a seat is drawn in — their own colour, or the faint default.
 *
 * A profile with no colour picked yet still has to be visible against the tile,
 * and every surface that paints a seat has to agree on what it falls back to.
 */
export function seatColour(participant: SessionParticipant): string {
  return participant.colour_snapshot || "var(--pb-ink-faint)";
}

// ---------------------------------------------------------------------------
// The radar
// ---------------------------------------------------------------------------

/**
 * One spoke per aspect, one polygon per seat.
 *
 * Every aspect gets an axis whether or not it has revealed, so the chart does
 * not rearrange itself under the reader as the night fills in — an aspect that
 * arrives late would otherwise rotate every other spoke to a new angle.
 *
 * The reveal gate is read from the aspect, never from the row. The server marks
 * the actor's OWN row visible before the reveal so the rating screen can show a
 * person what they answered, and plotting that would print this device's answers
 * to the next person handed the phone — on a shared device that is the whole
 * table's night, leaked one seat at a time. So a blind aspect contributes null
 * for everybody, including the seat this device is holding.
 *
 * A skip and an n/a are null too: the radar bridges across a missing spoke, and
 * a zero would put a person at the bottom of a ladder they declined to climb.
 *
 * The number plotted is the derived one, so a seat that answered six follow-ups
 * and never touched the topic itself has a point on that spoke rather than a
 * gap. Before this the shape a detailed rater drew was the emptiest one on the
 * chart, which is the opposite of what they did.
 */
export function radarValue(
  view: AspectView,
  followUps: readonly SubQuestionView[],
  participantId: string,
): number | null {
  if (!view.revealed) return null;
  const derived = topicValueOf(view, followUps, participantId);
  return derived.na ? null : derived.value;
}

export function seatRadar(state: SessionState): {
  axes: RadarAxis[];
  series: RadarSeries[];
} {
  const axes = state.aspects.map((view) => ({
    id: view.aspect.id,
    name: view.aspect.name_snapshot,
  }));
  const followUps = followUpsByAspect(state);
  const series = state.participants.map((participant) => {
    const points: Record<string, number | null> = {};
    for (const view of state.aspects) {
      points[view.aspect.id] = radarValue(
        view,
        followUps.get(view.aspect.id) ?? [],
        participant.id,
      );
    }
    return {
      id: participant.id,
      label: participant.name_snapshot,
      colour: seatColour(participant),
      points,
    };
  });
  return { axes, series };
}

/**
 * Has any spoke an answer on it yet?
 *
 * Nothing revealed yet is a different sentence from a night with shapes on it,
 * and the difference is not "no participants" — it is that every aspect is still
 * blind, so every point is null.
 */
export function radarHasShapes(series: readonly RadarSeries[]): boolean {
  return series.some((s) => Object.values(s.points).some((v) => typeof v === "number"));
}

export function headlineDistribution(state: SessionState): Distribution {
  const values: number[] = [];
  for (const r of state.headline.ratings) {
    if (!r.visible || typeof r.value !== "number") continue;
    values.push(r.value);
  }
  return distribution(values);
}

/** Who still owes the one mandatory number — the completion gate, previewed. */
export function missingHeadline(state: SessionState): SessionParticipant[] {
  const have = new Set(state.headline.ratings.map((r) => r.participant_id));
  return state.participants.filter((p) => !have.has(p.id));
}

export { aspectProfile, disagreement };
export type { AspectSlice, Distribution, OverallValue, RatingCell, TopicValue };

/**
 * Whether *this* device is the reason nothing has revealed.
 *
 * Pulled out of the rating screen because it is the difference between two
 * sentences that looked identical on screen and meant opposite things. "Everyone
 * answered but nothing revealed" has two causes: a seat that genuinely has not
 * arrived, or this phone's own writes never landing. The app used to assume the
 * first and tell the room to wait for the table — the one explanation the person
 * holding the phone can do nothing about, and the wrong one every time a push
 * had been refused.
 *
 * Mid-flight is not stuck. A queue that is syncing, or idle with nothing held
 * back, is working; only a refusal, or an offline device still carrying work,
 * means the answers are sitting on this device.
 */
export function deliveryStalled(input: {
  unsent: number;
  outbox: boolean;
  status: "idle" | "syncing" | "offline" | "paused" | "error";
  pending: number;
}): boolean {
  if (input.unsent > 0) return true;
  if (!input.outbox) return false;
  if (input.status === "error") return true;
  return input.status === "offline" && input.pending > 0;
}
